import React, { useEffect } from 'react';

import { ActionIcon, Drawer, DrawerProps, Flex, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';

export interface DroxyDrawerProps extends DrawerProps {
  title: string;
  icon: React.ReactNode;
  description: string;
  additionalControls?: React.ReactNode;
}

export function DroxyDrawer({
  title,
  description,
  icon,
  children,
  additionalControls,
  ...drawerProps
}: DroxyDrawerProps) {
  // Clear notifications when drawer is opened
  useEffect(() => {
    if (drawerProps.opened) notifications.clean();
  }, [drawerProps.opened]);

  return (
    <Drawer
      overlayProps={{ blur: 2, opacity: 0.6 }}
      size={600}
      withCloseButton={false}
      // Locking screen disables browser scroll inside the drawer for conversation view
      lockScroll={false}
      position="right"
      transitionProps={{ transition: 'slide-left', duration: 200, timingFunction: 'ease-out' }}
      styles={() => ({
        inner: { padding: 12 },
        content: {
          // Need this to override default Mantine behaviour
          borderRadius: '24px !important',
          display: 'flex',
          flexDirection: 'column'
        },
        body: {
          padding: 0,
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }
      })}
      {...drawerProps}>
      <Flex w="100%" pt={24} px={24} justify="space-between" style={{ position: 'sticky', zIndex: 1 }}>
        <Stack spacing={8}>
          <Flex align="center" gap={8}>
            {icon}
            <Title order={1} weight={600}>
              {title}
            </Title>
          </Flex>
          <Text color="dimmed" size="sm" mb={16}>
            {description}
          </Text>
        </Stack>

        <Flex justify="self-start" gap={6}>
          {additionalControls}
          <ActionIcon variant="subtle" onClick={drawerProps.onClose}>
            <IconX />
          </ActionIcon>
        </Flex>
      </Flex>
      {children}
    </Drawer>
  );
}
